import type { SvgIconComponent } from '@mui/icons-material';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import type { Theme } from '@mui/material';
import {
  DashboardPersonaBodyGoalEnum as DashboardGoal,
  DashboardPersonaBodyPersonaEnum as DashboardPersona,
} from 'src/openapi';

export { DashboardGoal, DashboardPersona };

type PersonaTone = 'gold' | 'blue' | 'navy';

interface PersonaMeta {
  label: string;
  Icon: SvgIconComponent;
  tone: PersonaTone;
  /** What the dashboard leads with, for the Settings menu. */
  summary: string;
}

export const PERSONA_META: Record<DashboardPersona, PersonaMeta> = {
  [DashboardPersona.Learner]: {
    label: 'Learner',
    Icon: MenuBookRoundedIcon,
    tone: 'gold',
    summary: 'Courses and skills first',
  },
  [DashboardPersona.JobFinder]: {
    label: 'Job finder',
    Icon: WorkOutlineRoundedIcon,
    tone: 'blue',
    summary: 'Jobs matched to your resume first',
  },
  [DashboardPersona.Experienced]: {
    label: 'Experienced',
    Icon: RocketLaunchRoundedIcon,
    tone: 'navy',
    summary: 'Roles at your level and applications first',
  },
};

export const isDashboardPersona = (value: unknown): value is DashboardPersona =>
  Object.values(DashboardPersona).includes(value as DashboardPersona);

/** The tile colours each persona is drawn in, read off the current theme. */
export const personaTone = (theme: Theme, persona: DashboardPersona) => {
  const gc = theme.palette.guidedCourse;
  return {
    gold: { bg: gc.gold, fg: gc.recommendedColor, shadow: gc.goldDeep },
    blue: { bg: gc.blue, fg: '#fff', shadow: gc.blueDeep },
    navy: {
      bg: gc.blueInk,
      fg: gc.gold,
      shadow: theme.darken(gc.blueInk, 0.5),
    },
  }[PERSONA_META[persona].tone];
};

/** The persona to draw the dashboard as. Unanswered reads as Learner, the
 * dashboard everyone had before personas existed. */
export const resolvePersona = (persona?: string | null): DashboardPersona =>
  isDashboardPersona(persona) ? persona : DashboardPersona.Learner;

export type DashboardSectionKey =
  | 'classInvites'
  | 'hero'
  | 'mobileApp'
  | 'careerCall'
  | 'courses'
  | 'competitions'
  | 'multiplayer'
  | 'skills'
  | 'resume'
  | 'jobs'
  | 'applications'
  | 'applySmarter'
  | 'hiddenJobs'
  // Not in the design, in the same card style.
  | 'community'
  | 'internships'
  | 'needsAttention'
  | 'preferences'
  | 'earnCredits'
  | 'referAndEarn'
  | 'chromeExtension'
  | 'promoCards';

/**
 * Everything the design leaves out, in the order it trails every dashboard.
 * The resume builder, referral and extension cards share one row.
 */
const TRAILING_SECTIONS: DashboardSectionKey[] = [
  'needsAttention',
  'preferences',
  'earnCredits',
  'resume',
  'referAndEarn',
  'chromeExtension',
  'promoCards',
];

/**
 * The design's section order per persona. Class invites stay first everywhere —
 * they come from a teacher and expire. The job hunter's matched jobs lead, then
 * courses, then its job-hunt summary beside the career call. Experienced has no jobs
 * feed: its hero is already the list of roles at its level. Elsewhere the
 * career call shares a row with skill levels.
 */
export const DASHBOARD_SECTION_ORDER: Record<
  DashboardPersona,
  DashboardSectionKey[]
> = {
  [DashboardPersona.Learner]: [
    'classInvites',
    'hero',
    'courses',
    'competitions',
    'mobileApp',
    'skills',
    'careerCall',
    'jobs',
    'multiplayer',
    'community',
    'applications',
    'internships',
    ...TRAILING_SECTIONS,
  ],
  [DashboardPersona.JobFinder]: [
    'classInvites',
    'jobs',
    'courses',
    'hero',
    'careerCall',
    'applySmarter',
    'hiddenJobs',
    'multiplayer',
    'community',
    'skills',
    'competitions',
    'mobileApp',
    ...TRAILING_SECTIONS,
  ],
  [DashboardPersona.Experienced]: [
    'classInvites',
    'hero',
    'applications',
    'courses',
    'competitions',
    'mobileApp',
    'multiplayer',
    'community',
    'skills',
    'careerCall',
    ...TRAILING_SECTIONS,
  ],
};

export type SectionWidth = 'full' | 'half' | 'third';

/**
 * How much of a row a section takes on a wide screen. Pairs sit next to each
 * other in every order above (job hunt + call, 1v1 + community, skills +
 * call, resume builder + referral + extension). When one of a pair renders nothing, or a
 * half has no partner (a hero followed by a full-width section), it grows into
 * the whole row rather than leaving a hole.
 */
export const DASHBOARD_SECTION_WIDTH: Partial<
  Record<DashboardSectionKey, SectionWidth>
> = {
  hero: 'half',
  careerCall: 'half',
  skills: 'half',
  multiplayer: 'half',
  community: 'half',
  resume: 'third',
  referAndEarn: 'third',
  chromeExtension: 'third',
};

export const PERSONA_GREETING: Record<DashboardPersona, string> = {
  [DashboardPersona.Learner]: 'Keep your streak going.',
  [DashboardPersona.JobFinder]: "Here's your job hunt.",
  [DashboardPersona.Experienced]: "Here's what's new at your level.",
};
